import { z } from 'zod';
import { requiredString, optionalString, coerceNumber, optionalNumber, requiredDate, optionalDate } from './helpers';

export const INSURANCE_PLAN_TYPES = ['laap', 'laanp', 'laac', 'ijm', 'lpp'] as const;
export type InsurancePlanType = typeof INSURANCE_PLAN_TYPES[number];

const lppAgeRateSchema = z.object({
  from: coerceNumber().pipe(z.number().min(17).max(70)),
  to: coerceNumber().pipe(z.number().min(18).max(70)),
  employee: coerceNumber().pipe(z.number().min(0).max(100)),
  employer: coerceNumber().pipe(z.number().min(0).max(100)),
});

export const insurancePlanSchema = z.object({
  type: z.enum(INSURANCE_PLAN_TYPES),
  valid_from: requiredDate(),
  valid_to: optionalDate(),
  insurer_name: requiredString(),
  policy_number: optionalString(),
  rates: z.object({
    employee: optionalNumber(),
    employer: optionalNumber(),
  }).default({ employee: null, employer: null }),
  laa_risk_class: optionalString(),
  lpp_plan: z.object({
    rates_by_age: z.array(lppAgeRateSchema).min(1),
    coordination_deduction_override: optionalNumber(),
    entry_threshold_override: optionalNumber(),
  }).nullable().optional().default(null),
}).superRefine((data, ctx) => {
  // LAAP requires employer rate
  if (data.type === 'laap') {
    if (data.rates.employer == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'rateEmployerRequired',
        path: ['rates', 'employer'],
      });
    }
  }

  // LAANP requires employee rate
  if (data.type === 'laanp') {
    if (data.rates.employee == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'rateEmployeeRequired',
        path: ['rates', 'employee'],
      });
    }
  }

  // LAAC/IJM require at least one rate
  if (data.type === 'laac' || data.type === 'ijm') {
    if (data.rates.employee == null && data.rates.employer == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'atLeastOneRateRequired',
        path: ['rates', 'employee'],
      });
    }
  }

  // LPP requires rates_by_age — nothing in rates
  if (data.type === 'lpp') {
    if (!data.lpp_plan || data.lpp_plan.rates_by_age.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'lppRatesByAgeRequired',
        path: ['lpp_plan', 'rates_by_age'],
      });
    }
  }

  // Age bracket validation: to >= from
  if (data.lpp_plan?.rates_by_age) {
    data.lpp_plan.rates_by_age.forEach((bracket, i) => {
      if (bracket.to < bracket.from) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'ageBracketToMustBeGreater',
          path: ['lpp_plan', 'rates_by_age', i, 'to'],
        });
      }
    });
  }
}).transform((data) => {
  // Nullify rates that don't apply for the given type
  if (data.type === 'laap') {
    data.rates.employee = null;
  } else if (data.type === 'laanp') {
    data.rates.employer = null;
  } else if (data.type === 'lpp') {
    data.rates = { employee: null, employer: null };
  }
  return data;
});

export type InsurancePlanFormData = z.input<typeof insurancePlanSchema>;
export type InsurancePlanPayload = z.output<typeof insurancePlanSchema>;
